export function clampVideoTime(video: HTMLVideoElement, time: number): number {
  const safeTime: number = Math.max(time, 0);
  const duration: number = Number.isFinite(video.duration) ? video.duration : 0;
  if (duration <= 0) return safeTime;
  return Math.min(safeTime, Math.max(duration - 0.1, 0));
}

const waitForVideoEvent = (
  video: HTMLVideoElement,
  eventName: 'loadedmetadata' | 'seeked',
): Promise<boolean> =>
  new Promise((resolve) => {
    const onEvent = () => {
      cleanup();
      resolve(true);
    };
    const onError = () => {
      cleanup();
      resolve(false);
    };
    const cleanup = () => {
      video.removeEventListener(eventName, onEvent);
      video.removeEventListener('error', onError);
    };
    video.addEventListener(eventName, onEvent);
    video.addEventListener('error', onError);
  });

export async function waitForVideoMetadata(video: HTMLVideoElement): Promise<boolean> {
  if (video.readyState >= 1) return true;
  return waitForVideoEvent(video, 'loadedmetadata');
}

export async function seekVideoTo(video: HTMLVideoElement, time: number): Promise<boolean> {
  const seekPromise: Promise<boolean> = waitForVideoEvent(video, 'seeked');
  video.currentTime = clampVideoTime(video, time);
  return seekPromise;
}

export async function captureVideoFrame(
  video: HTMLVideoElement,
  timestamp: number,
  options?: {
    quality?: number;
    restoreTime?: boolean;
    canvas?: HTMLCanvasElement;
  },
): Promise<string | null> {
  const hasMetadata: boolean = await waitForVideoMetadata(video);
  if (!hasMetadata) return null;

  const previousTime: number = video.currentTime;
  const seekOk: boolean = await seekVideoTo(video, timestamp);
  if (!seekOk) return null;

  const canvas: HTMLCanvasElement = options?.canvas ?? document.createElement('canvas');
  canvas.width = Math.max(video.videoWidth, 1);
  canvas.height = Math.max(video.videoHeight, 1);
  const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
  if (!context) return null;

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  const quality: number = options?.quality ?? 0.95;
  const dataUrl: string = canvas.toDataURL('image/jpeg', quality);

  if (options?.restoreTime !== false) {
    await seekVideoTo(video, previousTime);
  }

  return dataUrl;
}
